import PropTypes from "prop-types";
import React from "react";
import colors from "../styles/colors";
import styled from "styled-components";

const Button = ({ children, className, type, align, full, round, onClick, disabled, loading }) => {
  function getMargin() {
    if (align === "right") {
      return "0 0 16px auto";
    } else if (align === "center") {
      return "0 auto 16px";
    } else {
      return "0 0 16px";
    }
  }

  function getClassName() {
    var classes = [];
    if (className) {
      classes.push(className);
    }
    if (full) {
      classes.push("-full");
    }
    if (round) {
      classes.push("-round");
    }
    if (type === "outline") {
      classes.push("-outline");
    }
    if (disabled) {
      classes.push("-disabled");
    }
    if (classes.length > 0) {
      return classes.join(" ");
    }
  }

  switch (type) {
    case "link":
      return (
        <StyledButton
          className={getClassName()}
          bgColor="transparent"
          onClick={onClick}
          disabled={disabled}
          textColor={colors.primary}
          padding="8px 0"
          margin={getMargin()}
        >
          {children}
        </StyledButton>
      );
    case "outline":
      return (
        <StyledButton
          className={getClassName()}
          minWidth="74px"
          bgColor="transparent"
          onClick={onClick}
          disabled={disabled}
          textColor={colors.primary}
          margin={getMargin()}
          padding="8px 16px"
        >
          {children}
        </StyledButton>
      );
    case "secondary":
      return (
        <StyledButton
          className={getClassName()}
          minWidth="74px"
          bgColor={colors.secondary}
          onClick={onClick}
          disabled={disabled}
          textColor={colors.primary}
          margin={getMargin()}
          fontWeight="700"
          padding="8px 20px"
        >
          {children}
        </StyledButton>
      );
    case "loading":
      if (loading === true) {
        return (
          <StyledLoadingButton minWidth="100px">
            <StyledLoadingButtonContainer>
              <StyledLoadingButtonIcon></StyledLoadingButtonIcon>
            </StyledLoadingButtonContainer>
          </StyledLoadingButton>
        );
      } else {
        return (
          <StyledButton
            className={getClassName()}
            minWidth="100px"
            onClick={onClick}
            disabled={disabled}
            margin={getMargin()}
            padding="8px 20px"
          >
            {children}
          </StyledButton>
        );
      }
    default:
      return (
        <StyledButton
          className={getClassName()}
          minWidth="74px"
          margin={getMargin()}
          onClick={onClick}
          disabled={disabled}
        >
          {children}
        </StyledButton>
      );
  }
};

const StyledButton = styled.button.attrs((props) => ({
  bgColor: props.bgColor || colors.primary,
  textColor: props.textColor || colors.white,
  padding: props.padding || "10px 16px",
  margin: props.margin,
  minWidth: props.minWidth,
  fontWeight: props.fontWeight,
  onClick: props.onClick,
}))`
  display: block;
  cursor: pointer;
  font-size: 14px;
  border-radius: 6px;
  font-family: "Poppins", "Kanit", "Helvetica Neue", sans-serif;
  border: none;
  min-width: ${(props) => props.minWidth};
  background-color: ${(props) => props.bgColor};
  color: ${(props) => props.textColor};
  padding: ${(props) => props.padding};
  margin: ${(props) => props.margin};
  font-weight: ${(props) => props.fontWeight};
  box-sizing: border-box;

  &.-full {
    width: 100%;
  }

  &.-round {
    border-radius: 80px;
  }

  &.-outline {
    border: 1px solid ${(props) => props.textColor};
  }

  &.-disabled {
    opacity: 0.5;
  }

  &:only-child {
    margin-bottom: 0;
  }
`;

const StyledLoadingButton = styled.div.attrs((props) => ({
  bgColor: props.bgColor || colors.primary,
  textColor: props.textColor || colors.white,
  padding: props.padding || "10px 16px",
  margin: props.margin,
  minWidth: props.minWidth,
  fontWeight: props.fontWeight,
}))`
  display: block;
  cursor: wait;
  font-size: 14px;
  border-radius: 6px;
  font-family: "Poppins", "Kanit", "Helvetica Neue", sans-serif;
  border: none;
  min-width: ${(props) => props.minWidth};
  background-color: ${(props) => props.bgColor};
  color: ${(props) => props.textColor};
  padding: ${(props) => props.padding};
  margin: ${(props) => props.margin};
  font-weight: ${(props) => props.fontWeight};
  box-sizing: border-box;

  &.-full {
    width: 100%;
  }

  &.-round {
    border-radius: 80px;
  }
`;

const StyledLoadingButtonContainer = styled.div.attrs((props) => ({
  bgColor: props.bgColor || colors.primary,
  textColor: props.textColor || colors.white,
}))`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoadingButtonIcon = styled.div.attrs((props) => ({
  bgColor: props.bgColor || colors.primary,
  textColor: props.textColor || colors.white,
}))`
  border: 3px solid ${(props) => props.bgColor};
  border-top: 3px solid ${(props) => props.textColor};
  border-radius: 50%;
  width: 14px;
  height: 14px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  className: PropTypes.string,
  type: PropTypes.string,
  align: PropTypes.string,
  full: PropTypes.bool,
  round: PropTypes.bool,
};

export default Button;
